import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, api, unwrap } from "@/api/client";
import type { Recorder, Segment } from "./recorder";

/**
 * The recorder agent on the packing laptop (Plan Sprint 6): a small Windows
 * program listening on this laptop only. The packing page tells it when a
 * parcel starts and closes; it records the webcam or IP camera to its own
 * disk. Clips are played from it on that laptop, and asked to come up to
 * Zerpid only for complaints.
 */

export const AGENT_ORIGIN = "http://127.0.0.1:47615";

export type AgentStatus = {
  version: string;
  paired: boolean;
  removed: boolean;
  name: string;
  warehouseId: string;
  warehouseName: string;
  camera: string;
  /** the order being recorded; "" while the desk waits for its next parcel */
  recording: string;
  /** recording at all (agent 0.1.1 and up; older agents: since is set) */
  active?: boolean;
  since?: string;
  record: boolean;
  online: boolean;
  cameraError?: string;
  clips: number;
  freeBytes: number | null;
};

async function agentCall<T>(method: "GET" | "POST", path: string, body?: unknown, timeoutMs = 4000): Promise<T> {
  const ctl = new AbortController();
  const timer = window.setTimeout(() => ctl.abort(), timeoutMs);
  try {
    const res = await fetch(AGENT_ORIGIN + path, {
      method,
      signal: ctl.signal,
      headers: body ? { "Content-Type": "application/json" } : undefined,
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = (await res.json().catch(() => ({}))) as T & { error?: { code: string; message: string } };
    if (!res.ok) throw new ApiError(res.status, data.error?.code ?? "agent_error", data.error?.message ?? res.statusText);
    return data;
  } finally {
    window.clearTimeout(timer);
  }
}

export const agentStatus = () => agentCall<AgentStatus>("GET", "/api/status", undefined, 2500);

/**
 * The Recorder of a desk recording through the agent. Nothing is uploaded:
 * finish tells the agent which order the clip is, and the agent reports it.
 */
export function useAgentRecorder(enabled: boolean, warehouseId: string | undefined): Recorder & { status: AgentStatus | null } {
  const [status, setStatus] = useState<AgentStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);
  const running = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    let stop = false;
    const poll = () =>
      agentStatus().then(
        (s) => {
          if (stop) return;
          setStatus(s);
          // Recording without an order yet (the desk between parcels) is
          // still recording: starting again would cut a clip every poll.
          const on = s.active ?? !!(s.recording || s.since);
          setRecording(on);
          running.current = on;
          if (!s.paired) setError("agent_not_paired");
          else if (warehouseId && s.warehouseId && s.warehouseId !== warehouseId) setError("agent_other_warehouse");
          else if (!s.camera) setError("agent_no_camera");
          else if (s.cameraError) setError("agent_camera");
          else setError(null);
        },
        () => {
          if (stop) return;
          setStatus(null);
          setError("agent_unreachable");
        },
      );
    void poll();
    const timer = window.setInterval(() => void poll(), 5000);
    return () => {
      stop = true;
      window.clearInterval(timer);
    };
  }, [enabled, warehouseId]);

  const start = useCallback((label: string, orderId?: string) => {
    running.current = true;
    setRecording(true);
    void agentCall("POST", "/api/start", { orderId: orderId ?? "", externalId: orderId ? label : "" }, 10_000).catch((e: unknown) => {
      running.current = false;
      setRecording(false);
      setError(e instanceof ApiError ? e.message : "agent_unreachable");
    });
  }, []);
  const finish = useCallback(async (orderId: string, externalId: string): Promise<Segment | null> => {
    running.current = false;
    setRecording(false);
    await agentCall("POST", "/api/stop", { orderId, externalId }, 20_000).catch(() => undefined);
    return null;
  }, []);
  const stop = useCallback(async (): Promise<Segment | null> => {
    running.current = false;
    setRecording(false);
    await agentCall("POST", "/api/stop", {}, 20_000).catch(() => undefined);
    return null;
  }, []);
  const discard = useCallback(() => {
    if (!running.current) return;
    running.current = false;
    setRecording(false);
    void agentCall("POST", "/api/stop", { discard: true }, 20_000).catch(() => undefined);
  }, []);
  const setLabel = useCallback(() => undefined, []);
  const ready = enabled && error === null && status !== null;
  return { ready, error, recording, stream: null, start, setLabel, stop, finish, discard, status };
}

/**
 * A parcel's packing video: from the agent on this laptop when it keeps the
 * clip, else from Zerpid (which answers video_on_device when only a laptop
 * has it).
 */
export async function loadPackingVideo(orderId: string, location?: string): Promise<Blob> {
  if (location === "device") {
    try {
      const ctl = new AbortController();
      const timer = window.setTimeout(() => ctl.abort(), 4000);
      const res = await fetch(`${AGENT_ORIGIN}/api/clips/${orderId}`, { signal: ctl.signal });
      window.clearTimeout(timer);
      if (res.ok) return await res.blob();
    } catch {
      // not this laptop: ask Zerpid
    }
  }
  return unwrap(api.GET("/v1/packing/orders/{orderID}/video", { params: { path: { orderID: orderId } }, parseAs: "blob" }));
}

/** Asks the laptop keeping a clip to send it up to Zerpid. */
export const requestVideoUpload = (orderId: string) =>
  unwrap(api.POST("/v1/packing/orders/{orderID}/video/request", { params: { path: { orderID: orderId } } }));
